import { useState, useEffect } from 'react';
import { 
  FileText, AlertTriangle, MapPin, Activity, Loader2, Eye, 
  TrendingUp, TrendingDown, Clock, Zap, Radio, RefreshCw,
  ChevronRight, Shield, Bell
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth helper - supports both regular auth and Patreon tokens
const getAuthHeader = () => {
  const authType = localStorage.getItem('auth_type');
  if (authType === 'patreon') {
    const token = localStorage.getItem('patreon_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Category colors
const categoryColors = {
  'Weather': 'text-blue-400 bg-blue-500/10',
  'Supply Issues': 'text-amber-400 bg-amber-500/10',
  'Health': 'text-emerald-400 bg-emerald-500/10',
  'Unrest': 'text-red-400 bg-red-500/10',
  'Other': 'text-violet-400 bg-violet-500/10'
};

// Urgency colors
const urgencyColors = {
  high: { bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30' },
  medium: { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30' },
  low: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  normal: { bg: 'bg-zinc-800', text: 'text-zinc-400', border: 'border-zinc-700' }
};

// Confidence badge colors
const confidenceColors = {
  'High': 'bg-red-500/20 text-red-400',
  'Medium': 'bg-amber-500/20 text-amber-400',
  'Low': 'bg-zinc-700 text-zinc-400'
};

export const TodayBrief = ({ isSubscribed = true }) => {
  const [briefing, setBriefing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchBriefing = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/intelligence/briefing`, {
        headers: getAuthHeader()
      });
      setBriefing(response.data);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      console.error('Error fetching briefing:', err);
      setError('Unable to load intelligence briefing');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBriefing();
    // Refresh every 3 minutes
    const interval = setInterval(fetchBriefing, 3 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center space-y-4">
          <Loader2 className="w-10 h-10 mx-auto text-red-500 animate-spin" />
          <p className="text-zinc-500 text-sm">Loading intelligence briefing...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-zinc-900/50 rounded-lg border border-zinc-800">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-500" />
        <p className="text-zinc-400">{error}</p>
        <button 
          onClick={fetchBriefing}
          className="mt-4 text-red-400 hover:text-red-300 text-sm flex items-center gap-2 mx-auto"
        >
          <RefreshCw className="w-4 h-4" /> Try again
        </button>
      </div>
    );
  }

  const pulse = briefing?.pulse || {};
  const urgency = pulse.urgency || { level: 'normal', color: 'green', message: 'Normal activity' };
  const urgencyStyle = urgencyColors[urgency.level] || urgencyColors.normal;

  return (
    <div className="space-y-4" data-testid="today-dashboard">
      
      {/* Enhanced Live Pulse Section */}
      <section className={`bg-zinc-900 border ${urgencyStyle.border} rounded-lg overflow-hidden`}>
        <div className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative">
                <div className={`w-2.5 h-2.5 rounded-full animate-pulse ${urgency.level === 'high' ? 'bg-red-500' : urgency.level === 'medium' ? 'bg-amber-500' : 'bg-emerald-500'}`} />
              </div>
              <span className="font-heading font-semibold text-zinc-200 text-sm tracking-wide">LIVE PULSE</span>
            </div>
            {lastUpdated && (
              <span className="text-xs text-zinc-600">
                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
          
          {/* Stats Row */}
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
              <div className="text-2xl font-bold text-zinc-100">{pulse.signal_count || 0}</div>
              <div className="text-xs text-zinc-500">Signals / 24h</div>
            </div>
            <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
              <div className={`text-2xl font-bold flex items-center justify-center gap-1 ${(pulse.percent_change || 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {(pulse.percent_change || 0) >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                {Math.abs(pulse.percent_change || 0)}%
              </div>
              <div className="text-xs text-zinc-500">vs Yesterday</div>
            </div>
            <div className="text-center p-2 bg-zinc-800/50 rounded-lg">
              <div className="text-2xl font-bold text-zinc-100">{pulse.active_regions || 0}</div>
              <div className="text-xs text-zinc-500">Active Regions</div>
            </div>
          </div>
          
          {/* Urgency Message */}
          <div className={`flex items-center gap-2 p-2 rounded-md ${urgencyStyle.bg}`}>
            <Zap className={`w-4 h-4 ${urgencyStyle.text}`} />
            <span className={`text-sm ${urgencyStyle.text}`}>{urgency.message}</span>
          </div>
        </div>
      </section>

      {/* Today's Brief - Bullet Points */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-red-500" />
            <h2 className="font-heading font-semibold text-zinc-100 text-sm tracking-wide uppercase">Today's Brief</h2>
          </div>
        </div>
        <div className="p-4">
          <ul className="space-y-2 mb-3">
            {(briefing?.brief_bullets || []).map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-2 text-sm text-zinc-300">
                <ChevronRight className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" />
                <span>{bullet}</span>
              </li>
            ))}
          </ul>
          <div className="pt-2 border-t border-zinc-800/50">
            <span className="text-xs text-zinc-500">Pattern: </span>
            <span className="text-xs text-zinc-400">{briefing?.pattern_summary || 'Analysis in progress'}</span>
          </div>
        </div>
      </section>

      {/* Hot Zones */}
      {briefing?.hot_zones && briefing.hot_zones.length > 0 && (
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-amber-500" />
              <h2 className="font-heading font-semibold text-zinc-100 text-sm tracking-wide uppercase">Hot Zones</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {briefing.hot_zones.map((zone, idx) => (
                <div 
                  key={idx}
                  className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/70 rounded-full border border-zinc-700/50"
                >
                  <span className="text-zinc-200 text-sm font-medium">{zone.display}</span>
                  <span className="text-xs text-amber-400 font-mono">({zone.count})</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Top Signal - Enhanced Card */}
      {briefing?.top_signal && (
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h2 className="font-heading font-semibold text-zinc-100 text-sm tracking-wide uppercase">Top Signal</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded ${categoryColors[briefing.top_signal.category] || categoryColors['Other']}`}>
                  {briefing.top_signal.category}
                </span>
                {briefing.top_signal.is_urgent && (
                  <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">URGENT</span>
                )}
              </div>
              <span className={`text-xs px-2 py-0.5 rounded ${confidenceColors[briefing.top_signal.confidence] || confidenceColors['Low']}`}>
                {briefing.top_signal.confidence} Confidence
              </span>
            </div>
            <p className="text-zinc-200 text-sm leading-relaxed mb-2">
              {briefing.top_signal.short_description}
            </p>
            <div className="flex items-center gap-3 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {briefing.top_signal.time_ago}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {briefing.top_signal.city}, {briefing.top_signal.state?.substring(0, 2)?.toUpperCase()}
              </span>
            </div>
          </div>
        </section>
      )}

      {/* Emerging Pattern */}
      <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-violet-500" />
            <h2 className="font-heading font-semibold text-zinc-100 text-sm tracking-wide uppercase">Emerging Pattern</h2>
          </div>
        </div>
        <div className="p-4">
          <p className="text-zinc-200 text-sm mb-3">{briefing?.emerging_pattern?.summary || 'No clear pattern detected'}</p>
          {briefing?.emerging_pattern?.explanations?.length > 0 && (
            <div className="space-y-1.5">
              <span className="text-xs text-zinc-500 uppercase tracking-wide">Possible explanations:</span>
              {briefing.emerging_pattern.explanations.map((exp, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs text-zinc-400">
                  <span className="text-zinc-600">•</span>
                  <span>{exp}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Timeline */}
      {briefing?.timeline && briefing.timeline.length > 0 && (
        <section className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/80">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-blue-500" />
              <h2 className="font-heading font-semibold text-zinc-100 text-sm tracking-wide uppercase">Last 24 Hours</h2>
            </div>
          </div>
          <div className="p-4">
            <div className="space-y-3">
              {briefing.timeline.slice(0, 6).map((item, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <div className="w-14 flex-shrink-0">
                    <span className="text-xs font-mono text-zinc-500">{item.time}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-300 truncate">{item.description}</p>
                    <span className="text-xs text-zinc-600">{item.location}</span>
                  </div>
                  {item.is_urgent && (
                    <span className="text-xs text-red-400 flex-shrink-0">!</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Return Trigger */}
      <div className="text-center py-3 px-4 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
        <div className="flex items-center justify-center gap-2 text-zinc-500">
          <Radio className="w-4 h-4 text-red-500/70 animate-pulse" />
          <span className="text-sm">{briefing?.return_trigger || 'Signals updating in real time — check back soon'}</span>
        </div>
      </div>

      {/* Subtle Monetization Hook - only show if not subscribed */}
      {!isSubscribed && (
        <div className="text-center py-4 px-4 bg-gradient-to-r from-zinc-900 via-zinc-800/50 to-zinc-900 rounded-lg border border-zinc-700/30">
          <div className="flex items-center justify-center gap-2 text-zinc-400">
            <Shield className="w-4 h-4 text-red-500/70" />
            <span className="text-sm">Full signal feed + real-time alerts available inside</span>
          </div>
        </div>
      )}
    </div>
  );
};
